/**
 * resolveRenderSource — the single source of truth for deciding HOW a scanned
 * object is rendered in the 3D scene.
 *
 * Pure module: no React, no Firestore, no Three.js. The caller supplies the
 * environment (which types have a procedural renderer, which types are excluded
 * from the generic procedural path, and a sync lookup for a type's curated
 * default GLB) so this stays trivially unit-testable.
 *
 * Render-source priority (highest first):
 *   1. user GLB attached to the object            → kind: 'user-glb'
 *   2. curated DEFAULT GLB for the object's type   → kind: 'default-glb'  (ephemeral)
 *   3. procedural renderer for the type            → kind: 'procedural'
 *   4. plain fallback box                          → kind: 'fallback'
 *
 * An explicit opt-out (the user deliberately chose "procedural"/"none") is never
 * promoted to a default GLB: it skips priorities 1–2 and routes exactly as it
 * does today — procedural if a renderer exists, else the fallback box. See
 * `objectIsExplicitOptOut` for why a genuinely fresh/clean object does NOT match.
 */

import { resolveProceduralType } from '../geometry/proceduralRenderers/resolveProceduralType';
import { isKitchenSink, isSink } from '@shared-lib/sink-types';

import {
  objectHasGlbModel,
  objectIsExplicitOptOut,
  objectShowsGlb,
} from '@shared-lib/fixture-types/productRepresentation';

// One definition, shared with the cabinet-run engine
// (shared-lib/fixture-types/productRepresentation.js).
export { objectHasGlbModel, objectIsExplicitOptOut, objectShowsGlb };

/**
 * Kitchen appliances + the kitchen faucet. These are rendered by their own
 * `ProceduralObject` block in ArchitecturalScene when they carry no GLB, so
 * they are skipped by the SceneObject loop and by the generic procedural path.
 *
 * Single source of truth for BOTH of those checks — they used to be two
 * hand-maintained copies of the same list, and the copies drifted: the skip
 * side matched a scanned `{ category: { oven: {} } }` while the render side
 * read only `object.type` and defaulted to 'refrigerator', so every scanned
 * oven / stove / dishwasher drew as a fridge.
 *
 * Both old (stove / range-hood / oven) and new (range / vent-hood / wall-oven)
 * names are listed — structures saved before the GLB-aligned type rename flow
 * through the same paths.
 */
export const KITCHEN_APPLIANCE_TYPES = new Set([
  'refrigerator',
  'stove',
  'range',
  'cooktop',
  'dishwasher',
  'oven',
  'wall-oven',
  'kitchen-faucet',
  'range-hood',
  'vent-hood',
]);

/**
 * True when this object is one of the kitchen appliances rendered by
 * ArchitecturalScene's dedicated appliance block. Reads through
 * `resolveObjectType`, so a scanned object carrying only `category` matches
 * exactly the same way one carrying an explicit `type` does.
 */
export const isKitchenApplianceObject = (object) =>
  KITCHEN_APPLIANCE_TYPES.has(resolveObjectType(object));

/**
 * True when this object is a cabinet-family piece. Reads through
 * `resolveObjectType`, so `{ category: { 'oven-surround': true } }` matches
 * the same way an explicit `type` does.
 */
export const isCabinetObject = (object) =>
  CABINET_TYPES.has(resolveObjectType(object));

/**
 * The procedural renderer key for a cabinet-family object without a GLB —
 * which of ArchitecturalScene's cabinet renderers draws it. Corner-cabinet is
 * checked before lower-cabinet: it also carries the 'lower-cabinet' category
 * for clearance/draw purposes, but its 3D mesh is the L-shape, not a stock
 * box. Anything else in the family falls through to 'upper-cabinet'.
 */
export function resolveCabinetRendererType(object) {
  const type = object?.type?.toLowerCase() || '';
  const category =
    typeof object?.category === 'object' && object.category !== null
      ? object.category
      : {};
  if (type === 'fridge-surround' || category['fridge-surround']) {
    return 'fridge-surround';
  }
  if (type === 'laundry-surround' || category['laundry-surround']) {
    return 'laundry-surround';
  }
  if (type === 'oven-surround' || category['oven-surround']) {
    return 'oven-surround';
  }
  if (type === 'blank-panel' || category['blank-panel']) return 'blank-panel';
  if (type === 'corner-cabinet' || category['corner-cabinet']) {
    return 'corner-cabinet';
  }
  // The wedge at an angled corner carries 'lower-cabinet' too, for restyling;
  // its mesh is the polygon extrusion, not a box.
  if (type === 'corner-fill-cabinet' || category['corner-fill-cabinet']) {
    return 'corner-fill-cabinet';
  }
  if (type === 'lower-cabinet' || category['lower-cabinet']) {
    return 'lower-cabinet';
  }
  return 'upper-cabinet';
}

/**
 * Cabinet-family types, rendered by ArchitecturalScene's dedicated cabinet
 * block when they carry no GLB.
 *
 * Single source of truth for BOTH the SceneObject skip-check and the cabinet
 * block's include-filter. Those were two hand-maintained copies, and a type
 * missing from them — as `oven-surround` was — falls through to the generic
 * procedural path, which excludes cabinets, and renders as a plain fallback
 * box in the default finish while the run around it restyles. So was
 * `corner-fill-cabinet`, the wedge at an angled corner: drawn by its own
 * polygon renderer either way, but only this block hands it a finish.
 */
export const CABINET_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
  'corner-fill-cabinet',
  'fridge-surround',
  'laundry-surround',
  'oven-surround',
  'blank-panel',
]);

/**
 * Mirror-family types, rendered by ArchitecturalScene's dedicated `Mirror`
 * block (drei `Reflector`, real planar reflections) when they carry no GLB.
 *
 * `dorm-mirror` belongs here for the same reason `oven-surround` belongs in
 * CABINET_TYPES: it was absent, so it fell through to the generic procedural
 * path and drew as `mirrorRenderer`'s flat slab — a metalness-0.4 surface with
 * no environment map, which reads as a dull grey rectangle indistinguishable
 * from a blank poster. A Style-It dorm mirror never actually reflected
 * anything.
 *
 * Both this set and `isMirrorObject` are the single source of truth for the
 * THREE places that asked the question. ArchitecturalScene hand-rolled
 * `type === 'mirror' || category.mirror` twice, and `objectTypeUtils.isMirror`
 * is a third (dead) copy — none of which matched a `dorm-mirror` carrying
 * `category: { 'dorm-mirror': {} }`.
 */
export const MIRROR_TYPES = new Set(['mirror', 'dorm-mirror']);

/**
 * True when this object is a mirror rendered by the Reflector block. Reads
 * through `resolveObjectType`, so an object carrying only `category` matches
 * exactly as one carrying an explicit `type` does.
 */
export const isMirrorObject = (object) =>
  MIRROR_TYPES.has(resolveObjectType(object));

/**
 * Object types whose render is already handled by something other than the
 * generic procedural-fallback path — so they are excluded from BOTH the generic
 * procedural renderer AND ephemeral type defaults. Two flavors:
 *   - dedicated blocks (Fireplace, ProceduralObject for cabinets, Mirror with
 *     reflector, shower-niche, switches, kitchen appliances)
 *   - SceneObject's inline custom fallbacks (ceiling-light puck + recessed trim
 *     ring via proceduralStyle:'recessed') — preferred over the procedural one.
 * Single source of truth, imported by ArchitecturalScene and ElementPropertiesPanel.
 */
export const TYPES_EXCLUDED_FROM_GENERIC_PROCEDURAL = new Set([
  'fireplace',
  ...CABINET_TYPES,
  ...MIRROR_TYPES,
  'shower-niche',
  'switch',
  'dimmer',
  '3way',
  ...KITCHEN_APPLIANCE_TYPES,
  'ceiling-light',
]);

/**
 * Types that must never receive a curated EPHEMERAL default GLB.
 *
 * Split out of TYPES_EXCLUDED_FROM_GENERIC_PROCEDURAL, which was doing two
 * unrelated jobs: "something else already renders this" and "never give this a
 * default model". They coincided until mirrors needed to be BOTH — the Mirror
 * block owns a mirror's glass, and a curated default supplies its frame.
 *
 * Mirrors are therefore absent here. Nothing changes for bathroom mirrors
 * unless a catalog doc is actually flagged `isDefaultForType` for type
 * `mirror`; with none flagged the lookup negative-caches and the render path is
 * exactly as before.
 */
export const TYPES_EXCLUDED_FROM_TYPE_DEFAULTS = new Set(
  [...TYPES_EXCLUDED_FROM_GENERIC_PROCEDURAL].filter(
    (type) => !MIRROR_TYPES.has(type)
  )
);

/**
 * True when the object carries a usable user-attached GLB reference.
 * `modelId === 'none'` is the canonical "Original Geometry" sentinel and is NOT
 * a GLB. Moved here from ArchitecturalScene so there is one definition.
 */

/**
 * Resolve an object's type string. Mirrors SceneObject's
 * `objectType = object.type || getCategoryName(category)` so we catch objects
 * whose type is only encoded in a `{ sofa: true }` style category map.
 * Moved here from ArchitecturalScene so there is one definition.
 */
export const resolveObjectType = (object) => {
  if (object?.type) return object.type.toLowerCase();
  const category = object?.category;
  if (typeof category === 'string') return category.toLowerCase();
  if (typeof category === 'object' && category !== null) {
    const keys = Object.keys(category);
    if (keys.length > 0) return keys[0].toLowerCase();
  }
  return '';
};

/**
 * True when the user DELIBERATELY opted out of a GLB model for this object —
 * i.e. picked "procedural" or "None / Original Geometry" in the model picker.
 * Such objects must never be auto-promoted to a curated type default.
 *
 * A genuinely fresh, never-touched object carries none of these fields, so it
 * does NOT match (verified against the scan pipeline + applyModelUpdateToStructure):
 *   - `isProcedural`        — set only when the user chooses procedural for a
 *                             mirror/shower (applyModelUpdateToStructure).
 *   - `selectedModelType === 'procedural'` — generic procedural opt-out.
 *   - `selectedModelType === 'none'` — the user picked "None"/"Original Geometry"
 *                             (persisted as the canonical opt-out marker).
 *   - `modelId === 'none'`  — legacy / copy-time canonical "none" sentinel.
 */
/**
 * True when the scene actually shows this object's own GLB: it has a usable
 * model reference AND the user has not switched it to the built-in render.
 * Countertop.jsx and other dedicated blocks key on this instead of on
 * `modelUrl` alone — a built-in switch leaves no stale-URL ambiguity.
 */

/**
 * The countertop a sink sits IN — an EXISTING countertop it names as its
 * parent (the relation `getSinksOnCountertop` gathers each countertop's sinks
 * by) — or null. A sink naming a countertop that is gone is claimed by no
 * countertop, so it is not in one: it resolves like any other object and the
 * scene draws it un-cut rather than not at all.
 */
export const countertopHoldingSink = (object, roomObjects) => {
  if (!isSink(object)) return null;
  const id = (object.parents || []).find((p) => p?.type === 'countertop')?.id;
  if (id == null) return null;
  return (roomObjects || []).some((o) => o?.id === id) ? id : null;
};

/**
 * What a sink IN a countertop renders as. `Countertop.jsx` draws the sinks it
 * holds — the scene's model and procedural loops both skip them — and cuts
 * their holes from this same answer (`sinkCutoutSpec.js`): the sink's own
 * model while it shows one, else the built-in basin, the kitchen renderer's
 * for a kitchen sink and the bathroom placeholder's otherwise. NEVER the
 * catalog type default: the countertop does not draw one, so a panel that
 * reported it would name a product that is not on screen.
 */
export const resolveCountertopSinkSource = (sink) => {
  const type = isKitchenSink(sink) ? 'kitchen-sink' : 'sink';
  if (objectShowsGlb(sink)) {
    return {
      kind: 'user-glb',
      type,
      drawnBy: 'countertop',
      modelRef: {
        modelId: sink.modelId || null,
        modelUrl: sink.modelUrl || null,
        source: 'user',
      },
    };
  }
  return { kind: 'procedural', type, drawnBy: 'countertop' };
};

/**
 * Decide the render source for a single object.
 *
 * @param {Object} object - the scanned object.
 * @param {Object} ctx
 * @param {(type: string) => boolean} ctx.hasRenderer - has a procedural renderer.
 * @param {Set<string>} ctx.excludedTypes - types handled by dedicated blocks /
 *        SceneObject inline fallbacks, excluded from the generic procedural path.
 * @param {(type: string) => ({modelId: string, modelUrl: string}|null)} ctx.getTypeDefault
 *        - returns the curated default ref for a type, or null when there is no
 *        default OR it has not loaded yet (null naturally falls through to
 *        procedural, giving the procedural warm-up for free).
 * @param {Object} [options]
 * @param {Object[]} [options.roomObjects] - the room's objects, so a sink in a
 *        countertop is recognised (`countertopHoldingSink`).
 * @returns {{kind: 'user-glb'|'default-glb'|'procedural'|'fallback', type: string,
 *           drawnBy?: 'countertop',
 *           modelRef?: {modelId: string, modelUrl: string, source: 'user'|'default'}}}
 */
export const resolveRenderSource = (object, ctx, { roomObjects } = {}) => {
  // A sink in a countertop is the countertop's to draw — one rule for the
  // scene's loops, the countertop, its cut and the panels. Membership needs
  // the room's objects (does its countertop still exist?); a caller that
  // passes none is asking about an object no countertop can hold.
  if (countertopHoldingSink(object, roomObjects)) {
    return resolveCountertopSinkSource(object);
  }
  const { hasRenderer, excludedTypes, getTypeDefault } = ctx;
  // Two DIFFERENT exclusions, defaulting to one another for callers that only
  // care about the render path. "Nothing else renders this type" and "this type
  // may never take a curated default model" coincided until mirrors needed to
  // be excluded from the first and NOT the second: the Mirror block owns a
  // mirror's glass, while its frame comes from a default GLB.
  const defaultExcludedTypes = ctx.typeDefaultExcludedTypes || excludedTypes;
  // The RENDER type: `sink` fans out by `sinkType` (kitchen-sink,
  // wall-hung-sink), so a wall-mount unit never borrows the countertop
  // sink's curated default GLB and a kitchen sink looks up its own.
  const type = resolveProceduralType(resolveObjectType(object), object);
  const excluded = excludedTypes.has(type);
  const optOut = objectIsExplicitOptOut(object);

  // The procedural-or-box decision shared by the no-GLB branches below.
  // This is exactly today's behavior for an object without a GLB model.
  const proceduralOrFallback = () =>
    hasRenderer(type) && !excluded
      ? { kind: 'procedural', type }
      : { kind: 'fallback', type };

  // Opt-out: never promote to a default. Route exactly as today.
  if (optOut) {
    return proceduralOrFallback();
  }

  // Priority 1 — user-attached GLB.
  if (objectHasGlbModel(object)) {
    return {
      kind: 'user-glb',
      type,
      modelRef: {
        modelId: object.modelId || null,
        modelUrl: object.modelUrl || null,
        source: 'user',
      },
    };
  }

  // Priority 2 — curated DEFAULT GLB for the type (ephemeral, render-time only).
  if (!defaultExcludedTypes.has(type)) {
    const def = getTypeDefault(type);
    if (def) {
      return {
        kind: 'default-glb',
        type,
        modelRef: {
          modelId: def.modelId,
          modelUrl: def.modelUrl,
          // Measured glass aperture, when the catalog has one. Carried through
          // so the Mirror block can seat a Reflector on the real glass instead
          // of guessing a frame thickness.
          glassFrac: def.glassFrac || null,
          // The size this frame renders at, when it is fixed. `glassFrac` is a
          // fraction of the MODEL's box, and only a stretchable model takes the
          // object's declared box — so without this the reflector was sized
          // against a box the frame does not use.
          frameDimsM: def.frameDimsM || null,
          source: 'default',
        },
      };
    }
  }

  // Priority 3 / 4 — procedural renderer, else plain box.
  return proceduralOrFallback();
};
